import { LinearProgress, Typography, useMediaQuery } from '@mui/material'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import { alpha, darken, lighten, useTheme } from '@mui/material/styles'
import { Box, Stack } from '@mui/system'
import { useEffect, useRef, useState } from 'react'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'

const RING_SIZE = 86
const RING_STROKE = 7

/**
 * Formats a duration given in seconds into a compact, human-readable string.
 * Returns '' for unknown values so callers can hide the field gracefully.
 *
 * @param {number|null} totalSeconds
 */
const formatDuration = (totalSeconds) => {
  if (totalSeconds == null || Number.isNaN(totalSeconds)) return ''
  const s = Math.max(0, Math.round(totalSeconds))
  const hours = Math.floor(s / 3600)
  const minutes = Math.floor((s % 3600) / 60)
  const seconds = s % 60
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`
  if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, '0')}s`
  return `${seconds}s`
}

/**
 * Smoothly tweens a displayed number towards `target`. When `animate` is false
 * (reduced motion) it jumps straight to the value. Returns null while the
 * target is unknown so the caller can render an indeterminate state.
 *
 * @param {number|null} target
 * @param {boolean} animate
 */
const useCountUp = (target, animate) => {
  const [value, setValue] = useState(target ?? 0)
  const valueRef = useRef(target ?? 0)

  useEffect(() => {
    if (target == null) return undefined
    if (!animate) {
      valueRef.current = target
      setValue(target)
      return undefined
    }
    const from = valueRef.current
    const duration = 500
    let raf = 0
    let start
    const tick = (now) => {
      if (start === undefined) start = now
      const progress = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - progress, 3)
      const current = from + (target - from) * eased
      valueRef.current = current
      setValue(current)
      if (progress < 1) raf = window.requestAnimationFrame(tick)
    }
    raf = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(raf)
  }, [target, animate])

  return target == null ? null : value
}

/**
 * Reveals `text` character by character, like an LLM streaming its answer.
 * When `animate` is false (reduced motion) it shows the full text at once.
 *
 * @param {string} text
 * @param {boolean} animate
 */
const useTypewriter = (text, animate) => {
  const [output, setOutput] = useState(text)

  useEffect(() => {
    if (!animate || !text) {
      setOutput(text)
      return undefined
    }
    setOutput('')
    let i = 0
    const timer = setInterval(() => {
      i++
      setOutput(text.slice(0, i))
      if (i >= text.length) clearInterval(timer)
    }, 25)
    return () => clearInterval(timer)
  }, [text, animate])

  return output
}

/**
 * Hero progress ring. Renders a gradient-stroked SVG arc for the overall
 * progress and an indeterminate spinner when the value is unknown.
 *
 * @param {{ value: number|null, animate: boolean }} props
 */
const ProgressRing = ({ value, animate }) => {
  const theme = useTheme()
  const accent = theme.palette.primary.main
  const radius = (RING_SIZE - RING_STROKE) / 2
  const circumference = 2 * Math.PI * radius
  const indeterminate = value == null
  const clamped = Math.min(100, Math.max(0, value ?? 0))
  const offset = circumference * (1 - clamped / 100)
  const animatedPct = useCountUp(value, animate)

  return (
    <Box sx={{ position: 'relative', width: RING_SIZE, height: RING_SIZE, flexShrink: 0 }}>
      <Box
        component='svg'
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        sx={indeterminate && animate
          ? {
              '@keyframes ring-spin': { '100%': { transform: 'rotate(360deg)' } },
              animation: 'ring-spin 1.4s linear infinite'
            }
          : undefined}
      >
        <defs>
          <linearGradient id='plannerRingGradient' x1='0%' y1='0%' x2='100%' y2='100%'>
            <stop offset='0%' stopColor={darken(accent, 0.2)} />
            <stop offset='100%' stopColor={lighten(accent, 0.35)} />
          </linearGradient>
        </defs>
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={radius}
          fill='none'
          strokeWidth={RING_STROKE}
          stroke={alpha(accent, 0.12)}
        />
        <Box
          component='circle'
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={radius}
          fill='none'
          strokeWidth={RING_STROKE}
          strokeLinecap='round'
          stroke='url(#plannerRingGradient)'
          strokeDasharray={circumference}
          strokeDashoffset={indeterminate ? circumference * 0.7 : offset}
          sx={{
            transformBox: 'fill-box',
            transformOrigin: 'center',
            transform: 'rotate(-90deg)',
            transition: animate && !indeterminate ? 'stroke-dashoffset 0.6s cubic-bezier(0.16, 1, 0.3, 1)' : 'none'
          }}
        />
      </Box>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {indeterminate
          ? (
            <AutorenewIcon sx={{ fontSize: '1.5rem', color: alpha(accent, 0.8) }} />)
          : (
            <Stack direction='row' alignItems='baseline' sx={{ color: 'primary.main' }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(animatedPct ?? 0)}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', ml: 0.25, opacity: 0.7 }}>
                %
              </Typography>
            </Stack>)}
      </Box>
    </Box>
  )
}

/**
 * @param {{ plannerProgress: { working: boolean, progress: number|null, step: { code: string, label: string, pending: number|null, total: number|null }|null, executionCount: number, elapsedTime: number|null, estimatedRemainingTime: number|null }|null }} props
 *   elapsedTime and estimatedRemainingTime are expected in milliseconds.
 */
const PlannerProgressDisplay = ({ plannerProgress }) => {
  const { t_ } = useSmartTranslation()
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const animate = !reduceMotion

  const progress = plannerProgress?.progress ?? null
  const step = plannerProgress?.step ?? null
  const executionCount = plannerProgress?.executionCount ?? 0
  const stepTotal = step?.total ?? null
  const stepPending = step?.pending ?? null
  const stepDone = stepTotal != null ? stepTotal - (stepPending ?? 0) : 0
  const stepValue = stepTotal != null && stepTotal > 0 ? (stepDone / stepTotal) * 100 : 0
  // Backend sends these in milliseconds; formatDuration works in seconds.
  const elapsedTime = plannerProgress?.elapsedTime != null ? plannerProgress.elapsedTime / 1000 : null
  const remainingTime = plannerProgress?.estimatedRemainingTime != null ? plannerProgress.estimatedRemainingTime / 1000 : null
  const label = step?.label ?? ''
  const typedLabel = useTypewriter(label, animate)
  const isTyping = typedLabel.length < label.length
  const hasTimeInfo = elapsedTime != null || remainingTime != null

  return (
    <Box
      role='status'
      aria-live='polite'
      sx={(theme) => {
        const accent = theme.palette.primary.main
        return {
          position: 'fixed',
          bottom: 28,
          right: 28,
          zIndex: theme.zIndex.tooltip,
          width: '30rem',
          borderRadius: 3,
          border: `1px solid ${alpha(accent, 0.28)}`,
          backgroundColor: alpha(theme.palette.background.color_212121 ?? '#212121', 0.82),
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          boxShadow: `0 20px 48px ${alpha(theme.palette.common.black, 0.6)}, 0 0 0 1px ${alpha(accent, 0.1)}`,
          overflow: 'hidden',
          '@keyframes slide-up': {
            from: { opacity: 0, transform: 'translateY(24px) scale(0.98)' },
            to: { opacity: 1, transform: 'translateY(0) scale(1)' }
          },
          animation: animate ? 'slide-up 0.45s cubic-bezier(0.16, 1, 0.3, 1)' : 'none'
        }
      }}
    >
      <Stack sx={{ position: 'relative' }}>
        {/* Header */}
        <Stack direction='row' alignItems='center' gap={1.25} px={2.5} pt={2} pb={1.25}>
          <AutorenewIcon
            sx={{
              fontSize: '1.05rem',
              color: 'primary.main',
              flexShrink: 0,
              '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
              animation: animate ? 'spin 3s linear infinite' : 'none'
            }}
          />
          <Typography
            variant='body2'
            sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: '0.12em', flex: 1 }}
          >
            {t_('PLANNING')}
          </Typography>
          {executionCount > 1 && (
            <Typography
              variant='caption'
              sx={(theme) => ({
                color: 'primary.main',
                px: 0.9,
                py: 0.2,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                fontWeight: 600,
                lineHeight: 1.6,
                flexShrink: 0,
                fontVariantNumeric: 'tabular-nums'
              })}
            >
              #{executionCount}
            </Typography>
          )}
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'success.main',
              flexShrink: 0,
              '@keyframes pulse-dot': {
                '0%, 100%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.5)', transform: 'scale(1)' },
                '60%': { boxShadow: '0 0 0 7px rgba(76, 175, 80, 0)', transform: 'scale(1.15)' }
              },
              animation: animate ? 'pulse-dot 2.5s ease-out infinite' : 'none'
            }}
          />
        </Stack>

        {/* Body: progress ring + step details */}
        <Stack direction='row' alignItems='center' gap={2.5} px={2.5} pt={1} pb={2.5}>
          <ProgressRing value={progress} animate={animate} />

          <Stack flex={1} gap={1.5} sx={{ minWidth: 0 }}>
            <Box sx={{ minHeight: '2.6em', display: 'flex', alignItems: 'center' }}>
              <Typography
                variant='subtitle1'
                sx={{
                  lineHeight: 1.3,
                  color: 'text.primary',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {typedLabel}
                {isTyping && (
                  <Box
                    component='span'
                    sx={{
                      display: 'inline-block',
                      width: '0.55em',
                      height: '1.05em',
                      ml: '1px',
                      verticalAlign: 'text-bottom',
                      backgroundColor: 'primary.main',
                      borderRadius: '1px',
                      '@keyframes caret-blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } },
                      animation: 'caret-blink 0.9s step-end infinite'
                    }}
                  />
                )}
              </Typography>
            </Box>

            <Stack gap={0.5} sx={{ visibility: stepTotal != null ? 'visible' : 'hidden' }}>
              <LinearProgress
                variant='determinate'
                value={stepValue}
                sx={(theme) => ({
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: `linear-gradient(90deg, ${darken(theme.palette.primary.main, 0.25)}, ${theme.palette.primary.main}, ${lighten(theme.palette.primary.main, 0.35)})`,
                    boxShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.45)}`,
                    overflow: 'hidden',
                    '&::after': animate
                      ? {
                          content: '""',
                          position: 'absolute',
                          inset: 0,
                          background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.common.white, 0.3)}, transparent)`,
                          backgroundSize: '200% 100%',
                          '@keyframes shimmer': {
                            '0%': { backgroundPosition: '200% 0' },
                            '100%': { backgroundPosition: '-200% 0' }
                          },
                          animation: 'shimmer 2s linear infinite'
                        }
                      : undefined
                  }
                })}
              />
              <Typography variant='caption' sx={{ textAlign: 'right', color: 'text.secondary', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {stepTotal != null ? `${stepDone} / ${stepTotal}` : ''}
              </Typography>
            </Stack>

            {hasTimeInfo && (
              <Stack direction='row' alignItems='center' gap={1}>
                {elapsedTime != null && (
                  <Stack direction='row' alignItems='center' gap={0.5} sx={{ color: 'text.secondary' }}>
                    <AccessTimeRoundedIcon sx={{ fontSize: '0.95rem' }} />
                    <Typography variant='caption' sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {formatDuration(elapsedTime)}
                    </Typography>
                  </Stack>
                )}
                <Box sx={{ flex: 1 }} />
                {remainingTime != null && (
                  <Typography variant='caption' sx={{ color: 'primary.main', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {`≈ ${formatDuration(remainingTime)} ${t_('Remaining')}`}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  )
}

export default PlannerProgressDisplay
